import React, { useEffect, useMemo } from "react";
import { colors2, getVal, DateRangePickerV2, SwitchSingle, UI_BODY_SM } from "oolib";
import Moment from "moment";
import { DataFieldInputSelector } from "@/components/DataFieldInputSelector";
import { getPickerProp } from "../../utils/pickerVariantConfig";
import { useLineChartConfig } from "./ConfigForm_Context";
import { CollapseTransition } from "../../utils/CollapseTransition";

const isoToDateOrNull = (iso) => {
  if (!iso) return null;
  const m = Moment(iso);
  return m.isValid() ? m.toDate() : null;
};

const cyclicalOptions = [
  { display: "", value: false },
  { display: "", value: true }
];

export const ConfigForm_DataSection = () => {
  const {
    widgetConfig,
    dataKeys,
    dateFieldOptions,
    timeRangeOptions,
    restrictedIntervalOptions,
    dataBounds,
    selectedTimeRange,
    cyclicalValue,
    handleDateFieldChange,
    handleTimeRangeChange,
    handleCustomDateRangeChange,
    handleCyclicalChange,
    handleIntervalChange,
  } = useLineChartConfig();

  const customTimeRange = getVal(widgetConfig, dataKeys.customTimeRange) || { start: null, end: null };

  const timeRangeValue = useMemo(() => {
    if (!selectedTimeRange) return null;
    return { ...selectedTimeRange, dataType: "Time Range" };
  }, [selectedTimeRange]);

  const intervalValue = useMemo(() => {
    const raw = getVal(widgetConfig, dataKeys.interval) || restrictedIntervalOptions[0];
    if (!raw) return null;
    return { ...raw, dataType: "Frequency" };
  }, [widgetConfig, dataKeys.interval, restrictedIntervalOptions]);

  // Ensure oolib v2 datepicker portals exist outside overflow-hidden containers
  useEffect(() => {
    if (typeof document === "undefined") return;
    const ensurePortalDiv = (portalId) => {
      if (document.getElementById(portalId)) return;
      const el = document.createElement("div");
      el.id = portalId;
      document.body.appendChild(el);
    };
    ensurePortalDiv("date-picker-portal-start");
    ensurePortalDiv("date-picker-portal-end");
  }, []);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
      {/* Date Field Selection */}
      <div>
        <DataFieldInputSelector
          id={dataKeys.dateField}
          placeholder="X-Axis Property (Date Field)"
          placeholderIcon="CalendarBlank"
          options={dateFieldOptions}
          value={getVal(widgetConfig, dataKeys.dateField)}
          onChange={handleDateFieldChange}
          picker={getPickerProp({ widgetComp: 'WidgetLineChart', slot: 'dateField' })}
        />
      </div>

      {/* Time Range Selection */}
      <DataFieldInputSelector
        id={dataKeys.selectedTimeRange}
        placeholder="Time Range"
        placeholderIcon="Clock"
        options={timeRangeOptions}
        value={timeRangeValue}
        onChange={handleTimeRangeChange}
        trailingIcon="CaretRight"
        hideSearchHeader
      />

      {/* Custom Date Range Selection */}
      <CollapseTransition isVisible={selectedTimeRange?.value === "custom"}>
        <div>
          {dataBounds?.start && dataBounds?.end ? (
            <div>
              <DateRangePickerV2
                label="Custom date range"
                id={dataKeys.customTimeRange}
                withPortal={true}
                value={
                  customTimeRange?.start && customTimeRange?.end
                    ? [isoToDateOrNull(customTimeRange.start), isoToDateOrNull(customTimeRange.end)]
                    : [isoToDateOrNull(dataBounds.start), isoToDateOrNull(dataBounds.end)]
                }
                onChange={handleCustomDateRangeChange}
              />
              <div style={{ marginTop: "6px", color: colors2.grey60, fontSize: "12px" }}>
                Allowed range: {Moment(dataBounds.start).format("YYYY-MM-DD")} → {Moment(dataBounds.end).format("YYYY-MM-DD")}
              </div>
            </div>
          ) : (
            <div style={{ color: colors2.grey60, fontSize: "12px" }}>
              Date bounds aren't available yet. Open the chart once so data loads, then return here.
            </div>
          )}
        </div>
      </CollapseTransition>

      {/* Cyclical Toggle */}
      <div
        onClick={() => {
          const newVal = cyclicalValue?.value === true
            ? cyclicalOptions[0]
            : cyclicalOptions[1];
          handleCyclicalChange(dataKeys.cyclical, newVal);
        }}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '1rem',
          background: '#F7F7F7',
          borderRadius: '6px',
          cursor: 'pointer',
        }}
      >
        <UI_BODY_SM style={{ color: colors2.grey80 }}>Cyclical</UI_BODY_SM>
        <SwitchSingle
          id={dataKeys.cyclical}
          option={cyclicalOptions[1]}
          value={cyclicalValue?.value === true ? cyclicalOptions[1] : undefined}
          onChange={handleCyclicalChange}
          layoutStyle="style2"
        />
      </div>

      {/* Frequency Selection */}
      <DataFieldInputSelector
        id={dataKeys.interval}
        placeholder="Frequency"
        placeholderIcon="Timer"
        options={restrictedIntervalOptions}
        value={intervalValue}
        onChange={handleIntervalChange}
        trailingIcon="CaretRight"
        hideSearchHeader
      />
    </div>
  );
};
